package com.rosario.juridico.domain

import com.rosario.juridico.marketplace.model.NivelVerificacion
import com.rosario.juridico.platform.FirebaseProxy
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock
import kotlinx.serialization.Serializable

/**
 * Modelo de datos estructurado para el Perfil Público del Abogado en Firestore.
 */
@Serializable
data class PerfilAbogadoDataModel(
    val abogadoUid: String = "",
    val nombreCompleto: String = "",
    val matricula: String? = "",
    val telefono: String? = "",
    val jurisdiccion: String = "",
    val especialidades: List<String> = emptyList(),
    val biografia: String = "",
    val nivelVerificacion: String = NivelVerificacion.NIVEL_1_CREADA.name,
    val fechaSolicitudVerificacion: Long? = null,
    val fechaVerificacion: Long? = null,
    val motivoRechazo: String? = null
)

/**
 * Resultado de operaciones sobre el Perfil de Abogado.
 */
sealed class PerfilAbogadoResult<out T> {
    data class Success<out T>(val data: T) : PerfilAbogadoResult<T>()
    data class Error(val message: String, val cause: Throwable? = null) : PerfilAbogadoResult<Nothing>()
}

/**
 * Repositorio de la capa Data/Domain para gestionar los perfiles profesionales en 'perfiles_abogados'.
 */
class PerfilAbogadoRepository {

    /**
     * Guarda o actualiza el perfil del abogado logueado en Firestore.
     */
    suspend fun guardarPerfil(perfil: PerfilAbogadoDataModel): PerfilAbogadoResult<Unit> = withContext(Dispatchers.Default) {
        val uid = FirebaseProxy.getCurrentUserId()
            ?: return@withContext PerfilAbogadoResult.Error(
                message = "Sesión no válida. Inicia sesión para guardar tu perfil.",
            )

        try {
            val firestore = Firebase.firestore
            val perfilFinal = perfil.copy(abogadoUid = uid)

            firestore.collection("perfiles_abogados")
                .document(uid)
                .set(perfilFinal)

            PerfilAbogadoResult.Success(Unit)
        } catch (e: Exception) {
            PerfilAbogadoResult.Error(
                message = e.message ?: "Error de red al guardar el perfil en Firestore.",
                cause = e,
            )
        }
    }

    /**
     * Obtiene el perfil público del abogado desde Firestore.
     */
    suspend fun obtenerPerfil(uid: String? = null): PerfilAbogadoResult<PerfilAbogadoDataModel?> = withContext(Dispatchers.Default) {
        val targetUid = uid ?: FirebaseProxy.getCurrentUserId()
            ?: return@withContext PerfilAbogadoResult.Error(
                message = "No hay usuario para consultar el perfil.",
            )

        try {
            val firestore = Firebase.firestore
            val docSnapshot = firestore.collection("perfiles_abogados")
                .document(targetUid)
                .get()

            if (docSnapshot.exists) {
                val perfil = docSnapshot.data<PerfilAbogadoDataModel>()
                PerfilAbogadoResult.Success(perfil)
            } else {
                PerfilAbogadoResult.Success(null)
            }
        } catch (e: Exception) {
            PerfilAbogadoResult.Error(
                message = e.message ?: "Error de red al obtener el perfil desde Firestore.",
                cause = e,
            )
        }
    }

    /**
     * Obtiene todos los perfiles de abogados en estado NIVEL_2_PENDIENTE para el panel de administración.
     */
    suspend fun obtenerPerfilesPendientes(): PerfilAbogadoResult<List<PerfilAbogadoDataModel>> = withContext(Dispatchers.Default) {
        try {
            val firestore = Firebase.firestore
            val querySnapshot = firestore.collection("perfiles_abogados")
                .where { "nivelVerificacion" equalTo NivelVerificacion.NIVEL_2_PENDIENTE.name }
                .get()

            val lista = querySnapshot.documents.mapNotNull { doc ->
                try {
                    doc.data<PerfilAbogadoDataModel>().copy(abogadoUid = doc.id)
                } catch (e: Exception) {
                    val mat = try { doc.get<String?>("matricula") } catch (_: Exception) { null }
                    val tel = try { doc.get<String?>("telefono") } catch (_: Exception) { null }
                    val nom = try { doc.get<String?>("nombreCompleto") } catch (_: Exception) { null }
                    val niv = try { doc.get<String?>("nivelVerificacion") } catch (_: Exception) { null }
                    val fechaSol = try { doc.get<Long?>("fechaSolicitudVerificacion") } catch (_: Exception) { null }
                    PerfilAbogadoDataModel(
                        abogadoUid = doc.id,
                        nombreCompleto = nom.orEmpty().ifBlank { "Dr./Dra. Sin Nombre" },
                        matricula = mat,
                        telefono = tel,
                        nivelVerificacion = niv ?: NivelVerificacion.NIVEL_2_PENDIENTE.name,
                        fechaSolicitudVerificacion = fechaSol
                    )
                }
            }.filter { it.nivelVerificacion == NivelVerificacion.NIVEL_2_PENDIENTE.name }
            PerfilAbogadoResult.Success(lista)
        } catch (e: Exception) {
            PerfilAbogadoResult.Error(
                message = e.message ?: "Error de red al obtener perfiles pendientes desde Firestore.",
                cause = e,
            )
        }
    }

    /**
     * Aprueba la matrícula de un profesional cambiando su nivel a NIVEL_3_VERIFICADO.
     */
    suspend fun aprobarPerfilAbogado(uid: String): PerfilAbogadoResult<Unit> = withContext(Dispatchers.Default) {
        try {
            val firestore = Firebase.firestore
            val now = Clock.System.now().toEpochMilliseconds().toDouble()
            val updates = mapOf(
                "nivelVerificacion" to NivelVerificacion.NIVEL_3_VERIFICADO.name,
                "fechaVerificacion" to now
            )

            // 1. Actualizar perfiles_abogados/{uid}
            firestore.collection("perfiles_abogados").document(uid).update(updates)

            // 2. Actualizar abogados/{uid}
            try {
                firestore.collection("abogados").document(uid).update(updates)
            } catch (e: Exception) {
                println("Aviso al aprobar en 'abogados' ($uid): ${e.message}")
            }

            // 3. Actualizar users/{uid}
            try {
                firestore.collection("users").document(uid).update("nivelVerificacion" to NivelVerificacion.NIVEL_3_VERIFICADO.name)
            } catch (e: Exception) {
                println("Aviso al aprobar en 'users' ($uid): ${e.message}")
            }

            PerfilAbogadoResult.Success(Unit)
        } catch (e: Exception) {
            PerfilAbogadoResult.Error(
                message = e.message ?: "Error al aprobar la verificación en Firestore.",
                cause = e,
            )
        }
    }
}
